import React, { useState, useEffect, useRef } from "react";
import { useNavigate }    from "react-router-dom";
import { Nav, Tab }       from "react-bootstrap";
import Spinner            from "react-bootstrap/Spinner";
import queryString        from "query-string";
import moment             from "moment";
import WalletService      from "services/wallet.service";
import AuthService        from "services/auth.service";
import { formatCurrency } from "helpers/common.helper";
import DownloadIcon       from "assets/icons/Download.svg";
import FilterIcon         from "assets/icons/filterIcon.svg";
import WalletIcon         from "assets/icons/wallet-color.svg";
import ArrowRightIcon     from "assets/icons/right-arrow.svg";
import TransactionFilterModal from "./TransactionFilterModal";
import "./Wallet.scss";

const TABS = [
  { key: "all",        label: "All"        },
  { key: "processing", label: "Processing" },
  { key: "success",    label: "Success"    },
  { key: "failed",     label: "Failed"     },
];

const PER_PAGE = 10;

const defaultMetadata = { total_page: 1, current_page: 1, per_page: PER_PAGE, total_item: 0, withdrawal_balance: 0, actual_withdrawal_balance: 0 };

const TYPE_LABELS = { payment_in: "Payment", withdraw: "Withdraw" };

const STATUS_LABELS = { on_hold: "Processing" };

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");

const statusLabel = (status) => STATUS_LABELS[status] || capitalize(status);

const TransactionTable = ({ transactions }) => {
  if (!transactions || !transactions.length) {
    return <div className="no-result">No result</div>;
  }

  return (
    <table className="transaction-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Payment Status</th>
          <th>Transaction Type</th>
          <th>Reference No.</th>
          <th>Description</th>
          <th>Amount (RM)</th>
          <th>Wallet Balance</th>
        </tr>
      </thead>
      <tbody>
        { transactions.map((tx) => {
          const isDebit = tx.transaction_type === "withdraw";
          const sign    = isDebit ? "-" : "+";

          return (
            <tr key={ tx.id }>
              <td>{ moment(tx.created_at).format("YYYY-MM-DD") }</td>
              <td>{ statusLabel(tx.status) }</td>
              <td>{ TYPE_LABELS[tx.transaction_type] || tx.transaction_type }</td>
              <td>{ tx.reference_no }</td>
              <td>{ tx.description }</td>
              <td>{ sign + "RM " + formatCurrency(Math.abs(tx.amount)) }</td>
              <td>RM { formatCurrency(tx.wallet_balance) }</td>
            </tr>
          );
        }) }
      </tbody>
    </table>
  );
};

const Wallet = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [metadata, setMetadata]       = useState(defaultMetadata);
  const [statusCounts, setStatusCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus]           = useState("all");
  const [startDate, setStartDate]     = useState("");
  const [endDate, setEndDate]         = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [showFilter, setShowFilter]   = useState(false);
  const [loader, setLoader]           = useState(false);
  const isServiceCancelled            = useRef(false);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      getTransactions(1);
    }
    return () => {
      isServiceCancelled.current = true;
    };
  }, [status, startDate, endDate, transactionType]); // eslint-disable-line

  useEffect(() => {
    getTransactions(currentPage);
    return () => {
      isServiceCancelled.current = true;
    };
  }, [currentPage]); // eslint-disable-line

  function handleError(error) {
    if (error.response && error.response.status === 401) {
      AuthService.logout();
      window.location.replace("/eb/login");
    }
  }

  function getTransactions(page) {
    setLoader(true);

    const parameters = queryString.stringify({
      page:             page,
      per:              PER_PAGE,
      status:           status === "all" ? undefined : status,
      start_date:       startDate || undefined,
      end_date:         endDate || undefined,
      transaction_type: transactionType || undefined,
    });

    WalletService.transactions(parameters).then(
      (data) => {
        setTransactions(data.wallet_transactions ? data.wallet_transactions : []);
        setMetadata(data.metadata ? data.metadata : defaultMetadata);
        setStatusCounts(data.status_counts ? data.status_counts : {});
      },
      (error) => handleError(error)
    ).then(() => {
      setLoader(false);
    });
  }

  const renderPageButtons = () => {
    const totalPages = metadata.total_page;
    const buttons    = [];

    buttons.push(
      <button
        key="prev"
        className="page-btn"
        disabled={ currentPage === 1 }
        onClick={ () => setCurrentPage(p => Math.max(1, p - 1)) }
      >
        &lt;
      </button>
    );

    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3, "ellipsis", totalPages);
    }

    pages.forEach((page) => {
      if (page === "ellipsis") {
        buttons.push(
          <span key="ellipsis" className="page-btn" style={{ border: "none", cursor: "default" }}>...</span>
        );
      } else {
        buttons.push(
          <button
            key={ page }
            className={ `page-btn${currentPage === page ? " active" : ""}` }
            onClick={ () => setCurrentPage(page) }
          >
            { page }
          </button>
        );
      }
    });

    buttons.push(
      <button
        key="next"
        className="page-btn"
        disabled={ currentPage === totalPages }
        onClick={ () => setCurrentPage(p => Math.min(totalPages, p + 1)) }
      >
        &gt;
      </button>
    );

    return buttons;
  };

  const handleApplyFilter = ({ startDate: start, endDate: end, transactionType: type }) => {
    setStartDate(start || "");
    setEndDate(end || "");
    setTransactionType(type || "");
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setTransactionType("");
    setShowFilter(false);
  };

  const handleDownload = () => {
    const parameters = queryString.stringify({
      status:           status === "all" ? undefined : status,
      start_date:       startDate || undefined,
      end_date:         endDate || undefined,
      transaction_type: transactionType || undefined,
    });

    WalletService.downloadCsv(parameters).then(
      (data) => {
        const url  = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "wallet-transactions.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      },
      (error) => handleError(error)
    );
  };

  const totalCount = metadata.total_item;
  const startItem  = totalCount === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const endItem    = Math.min(currentPage * PER_PAGE, totalCount);

  return (
    <div id="main-content-target" className="wallet-page">
      <h1 className="text-danger mb-4">My Wallet</h1>

      <div className="wallet-summary-cards">
        <div className="balance-card">
          <p className="mb-1">Wallet balance</p>
          <h2>RM { formatCurrency(metadata.withdrawal_balance) }</h2>
          { metadata.actual_withdrawal_balance !== metadata.withdrawal_balance &&
            <p className="actual-balance mb-0">Withdrawable balance: RM { formatCurrency(metadata.actual_withdrawal_balance) }</p>
          }
          <div className="wallet-illustration-wrap">
            <img src={ WalletIcon } alt="wallet" />
          </div>
        </div>

        <div
          className="withdraw-card"
          onClick={ () => navigate("/eb/wallet/withdraw", { state: { balance: metadata.actual_withdrawal_balance } }) }
        >
          <p className="mb-0">Withdraw</p>
          <img src={ ArrowRightIcon } alt="withdraw" className="withdraw-arrow" />
        </div>
      </div>

      <div className="transaction-history">
        <h2>Transaction History</h2>

        <Tab.Container id="wallet-tabs" defaultActiveKey="all" onSelect={ (k) => setStatus(k) }>
          <Nav variant="pills">
            <button className="btn filter-btn" onClick={ () => setShowFilter(true) }>
              <img src={ FilterIcon } alt="filter" />
            </button>

            <div className="custom-tab-items">
              { TABS.map(tab => (
                <Nav.Item key={ tab.key }>
                  <Nav.Link eventKey={ tab.key }>
                    { tab.label } ({ statusCounts[tab.key] || 0 })
                  </Nav.Link>
                </Nav.Item>
              )) }
            </div>

            <button className="btn download-btn" onClick={ handleDownload }>
              <img src={ DownloadIcon } alt="download" />
              Download
            </button>
          </Nav>

          <div className="loader conditional-display" displayed={ loader.toString() }>
            <Spinner animation="border" className="spinner" />
          </div>

          <Tab.Content className="conditional-display" displayed={ (!loader).toString() }>
            { TABS.map(tab => (
              <Tab.Pane key={ tab.key } eventKey={ tab.key }>
                <TransactionTable transactions={ transactions } />
              </Tab.Pane>
            )) }
          </Tab.Content>
        </Tab.Container>

        { totalCount > 0 &&
          <div className="pagination-bar">
            <span className="page-info">
              Showing <b>{ startItem }-{ endItem }</b> of <b>{ totalCount }</b>
            </span>

            <div className="page-controls">
              { renderPageButtons() }
            </div>
          </div>
        }
      </div>

      <TransactionFilterModal
        show={ showFilter }
        onClose={ () => setShowFilter(false) }
        filters={ { startDate, endDate, transactionType } }
        onApply={ handleApplyFilter }
        onClear={ handleClearFilter }
      />
    </div>
  );
};

export default Wallet;
